import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { ACCENT_COLOR_DEFAULT_INDEX_MAP, INSTALLATION_COLOR_LIST } from '../const';
import {
  CurrentInstallationQueryVariables,
  InstallationGetAllQuery,
  InstallationGetAllQueryVariables,
  IntegrationType,
  Query,
  SelectInstallationMutation,
  SelectInstallationMutationVariables,
  UserInstallation,
  UserInstallationsCollectionSegment,
} from '../data-access/gql-types/graphql';
import { SELECT_INSTALLATION } from '../data-access/mutations/installations';
import { CURRENT_INSTALLATION, INSTALLATION_GET_ALL } from '../data-access/queries/installations';
import { ON_INSTALLATION_DETACH } from '../data-access/subscriptions/on-installation-detach';
import { environment } from '../environment';
import { useSentry } from '../hooks/sentry/use-sentry';
import { useExalusContext } from '../integrations/exalus/context';
import { useExalusMigrationData } from '../integrations/exalus/hooks/common/use-migration-data';
import { useExalusConnection } from '../integrations/exalus/hooks/connection';
import { getExalusInstallationCredentials } from '../integrations/exalus/utils';
import { useDevicesAndChannelsContext } from '../integrations/lavva/context/devices-and-channels';
import { useVerifyCacheData } from '../integrations/lavva/hooks/cache';
import * as storage from '../integrations/lavva/storage';
import { setCacheData } from '../integrations/lavva/storage/cache';
import { useCacheStore } from '../integrations/lavva/store/cache';
import { useGroupsStore } from '../integrations/lavva/store/groups';
import { useInstallationsStore } from '../store/installations';
import { ChildrenProps, InstallationState } from '../types';
import { isLocalApp } from '../utils/helpers/local-app';
import { setBodyStyle } from '../utils/theme';
import { toastError } from '../utils/toast';
import { useBackdropContext } from './backdrop';
import { useGraphqlContext } from './graphql';

const initialState: InstallationState = {
  selectedInstallationId: window.localStorage.getItem('selectedInstallationId') || undefined,
  setSelectedInstallationId: () => null,
  installationList: [],
  installationsFetched: false,
  updateInstallationList: () => null,
  selectedInstallation: undefined,
  installationsLoading: false,
  currentInstallationLoading: false,
  skipLavvaFetch: false,
  hardRedirect: false,
  currentInstallationRefetch: () => null,
  selectInstallation: () => null,
  integrationType: IntegrationType.Undefined,
};

export const InstallationContext = createContext(initialState);
export const useInstallationContext = (): InstallationState => useContext(InstallationContext);

const InstallationContextProvider: React.FC<ChildrenProps> = ({ children }) => {
  const { t: tc } = useTranslation('common');
  const [listCacheEnabled, setListCacheEnabled] = useState<boolean>(false);
  const [installationList, setInstallationList] = useState<UserInstallation[] | null>(null);
  const [selectedInstallation, setSelectedInstallation] = useState(initialState.selectedInstallation);
  const [selectedInstallationId, setCurrentInstallationId] = useState(initialState.selectedInstallationId);
  const [integrationType, setIntegrationType] = useState<IntegrationType>(initialState.integrationType);
  const { setSelectedExalusCredentials } = useExalusContext();
  const { goToInstallationList, goToExalusLogin } = useExalusConnection();
  const { setSentryTags } = useSentry();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { setDeviceList, setChannelList } = useDevicesAndChannelsContext();
  const clearGroups = useGroupsStore((s) => s.clearGroups);
  const { shouldRefetchInstallations, resetRefetchInstallations } = useInstallationsStore((s) => s);
  const { updateBrokerUrl } = useGraphqlContext();
  const { installationsSkip, setActionsSkip, setGroupsSkip } = useCacheStore((s) => s);
  const { checkIfExalusMigrationAvailableLocal, checkIfExalusMigrationAvailable } = useExalusMigrationData();
  const [changeInstallation] = useMutation<SelectInstallationMutation, SelectInstallationMutationVariables>(
    SELECT_INSTALLATION,
  );
  const {
    data: installationsData,
    loading: installationsLoading,
    refetch: refetchInstallations,
  } = useQuery<InstallationGetAllQuery, InstallationGetAllQueryVariables>(INSTALLATION_GET_ALL, {
    fetchPolicy: 'cache-and-network',
    skip: isLocalApp || installationsSkip,
  });
  const {
    data: currentInstallationData,
    refetch: currentInstallationRefetch,
    loading: currentInstallationLoading,
  } = useQuery<Query, CurrentInstallationQueryVariables>(CURRENT_INSTALLATION, {
    fetchPolicy: 'no-cache',
    skip: isLocalApp,
  });

  const { data: installationDetachData } = useSubscription(ON_INSTALLATION_DETACH, {
    variables: { installationId: selectedInstallationId },
    skip: !selectedInstallationId,
  });

  useEffect(() => {
    if (shouldRefetchInstallations) {
      refetchInstallations();
      resetRefetchInstallations();
    }
  }, [shouldRefetchInstallations, refetchInstallations, resetRefetchInstallations]);

  const updateInstallationList = (list: UserInstallation[]) => {
    setInstallationList(list);
    storage.setLocalItem(
      'installationList',
      list.filter((x) => x.integrationType === IntegrationType.Exalus),
    );
  };

  const localAppInstallationsSets = async () => {
    turnOnBackdrop();
    await checkIfExalusMigrationAvailableLocal();
    const localInstallations = storage.getLocalItem('installationList');

    if (localInstallations?.length) {
      const installationId = localStorage.getItem('selectedInstallationId');

      if (installationId) {
        const found = localInstallations.find((x) => x.installationId === installationId);

        if (found) setSelectedInstallationId(found.installationId);
        else {
          goToInstallationList();
        }
      } else {
        setSelectedInstallationId(localInstallations[0].installationId);
      }
      updateInstallationList(localInstallations);
      turnOffBackdrop();
    } else {
      goToExalusLogin();
      turnOffBackdrop();
    }
  };

  useEffect(() => {
    if (isLocalApp) localAppInstallationsSets();
  }, []);

  useEffect(() => {
    if (
      installationDetachData?.onInstallationDetach?.newBrokerUrl &&
      installationDetachData?.onInstallationDetach?.installationId
    ) {
      if (selectedInstallationId === installationDetachData?.onInstallationDetach?.installationId) {
        updateBrokerUrl(installationDetachData?.onInstallationDetach?.newBrokerUrl);
      }
    }
  }, [installationDetachData]);

  const authorizedAppInstallationsSet = async (userInstallations: UserInstallation[]) => {
    turnOnBackdrop();
    const list = (await checkIfExalusMigrationAvailable(userInstallations)) as UserInstallation[];
    updateInstallationList(list);
    turnOffBackdrop();

    const foundSelected = userInstallations.find((x) => x.isSelected);
    setSelectedInstallationId(foundSelected?.installationId);
  };

  const { verifyCacheData } = useVerifyCacheData({ updateInstallationList, setListCacheEnabled });

  useEffect(() => {
    if (installationsData?.userInstallations) {
      setCacheData({
        kind: 'common',
        commonData: { installations: installationsData.userInstallations as UserInstallationsCollectionSegment },
      });

      authorizedAppInstallationsSet((installationsData.userInstallations.items as UserInstallation[]) || []);
    }
  }, [installationsData]);

  useEffect(() => {
    if (currentInstallationData?.currentInstallation?.brokerUrl) {
      updateBrokerUrl(currentInstallationData.currentInstallation.brokerUrl);
    }

    if (!currentInstallationLoading) verifyCacheData(currentInstallationData?.currentInstallation || null);
  }, [currentInstallationData?.currentInstallation]);

  useEffect(() => {
    currentInstallationRefetch();
  }, [selectedInstallationId]);

  useEffect(() => {
    if (installationList) {
      const foundInstallation = installationList.find((x) => x.installationId === selectedInstallationId);
      setSelectedInstallation(
        foundInstallation
          ? { ...foundInstallation, payload: currentInstallationData?.currentInstallation?.payload }
          : undefined,
      );
      setIntegrationType(foundInstallation?.integrationType || IntegrationType.Undefined);

      setSentryTags({
        installationId: foundInstallation?.installationId || '-',
        installationName: foundInstallation?.name || '-',
        integrationType: foundInstallation?.integrationType || '-',
        version: environment.VERSION || '-',
      });

      if (foundInstallation) {
        if (foundInstallation?.integrationType === IntegrationType.Exalus) {
          const credentials = getExalusInstallationCredentials(foundInstallation.installationId);

          if (credentials) setSelectedExalusCredentials(credentials);
          else goToInstallationList();
        }
      }
    }
  }, [selectedInstallationId, installationList, currentInstallationData]);

  const hardRedirect = useMemo(
    () =>
      !!(installationList && installationList.filter((x) => x.integrationType === IntegrationType.Exalus).length > 1),
    [installationList],
  );

  useEffect(() => {
    if (!selectedInstallation) return;

    setBodyStyle(
      '--color-accent',
      selectedInstallation.hexColor ||
        INSTALLATION_COLOR_LIST[ACCENT_COLOR_DEFAULT_INDEX_MAP[environment.INTEGRATION_DEFAULT]],
    );
  }, [selectedInstallation?.hexColor]);

  const setSelectedInstallationId = (installationId: string | undefined) => {
    setCurrentInstallationId(installationId);

    if (installationId) window.localStorage.setItem('selectedInstallationId', installationId);
    else window.localStorage.removeItem('selectedInstallationId');
  };

  const selectInstallation = (installationId: string | undefined, onSuccess?: () => void) => {
    turnOnBackdrop();

    if (selectedInstallationId !== installationId) {
      setDeviceList([]);
      setChannelList([]);
      clearGroups();
    }

    setActionsSkip(true);
    setGroupsSkip(true);

    changeInstallation({
      variables: { installationId },
      onCompleted: (data) => {
        if (data?.selectInstallation) {
          if (data.selectInstallation.brokerUrl) updateBrokerUrl(data.selectInstallation.brokerUrl);
          setSelectedInstallationId(data.selectInstallation.installationId || undefined);
          onSuccess?.();
        } else {
          turnOffBackdrop();
          toastError({ content: tc('errors.somethingWentWrong') });
        }
      },
      onError: () => turnOffBackdrop(),
    });
  };

  const skipLavvaFetch =
    !selectedInstallationId ||
    integrationType === IntegrationType.Exalus ||
    integrationType === IntegrationType.Undefined;

  const values: InstallationState = {
    selectedInstallationId,
    selectedInstallation,
    installationList: installationList || [],
    installationsFetched: installationList !== null,
    installationsLoading: listCacheEnabled || isLocalApp ? false : !!(installationsLoading || installationsSkip),
    currentInstallationLoading: isLocalApp ? false : currentInstallationLoading,
    setSelectedInstallationId,
    updateInstallationList,
    currentInstallationRefetch,
    selectInstallation,
    hardRedirect,
    integrationType,
    skipLavvaFetch,
  };

  return <InstallationContext.Provider value={values}>{children}</InstallationContext.Provider>;
};

export default InstallationContextProvider;
